<template>
	<div
		class="c-GroupedDateTimeRange"
		:class="groupedDateTimeRangeObjClass">
		<Select
			class="c-GroupedDateTimeRange__select"
			:label="selectLabel"
			:select-items="selectItems"
			:data="selectData"
			:with-clear="false"
			:disabled="disabled"
			:is-active="isActive"
			:style="selectStyleObj"
			@change="handleSelectChange"></Select>
		<DateTimeRange
			class="c-GroupedDateTimeRange__input"
			from-label="From"
			to-label="To"
			:data="rangeData"
			:only-time="onlyTime"
			:only-date="onlyDate"
			:input-format="inputFormat"
			:field-format="fieldFormat"
			:output-format="outputFormat"
			:disabled="disabled"
			:is-active="isActive"
			@change="handleDateChange">
		</DateTimeRange>
	</div>
</template>

<script>
import Select from '../Select/Select.vue'
import DateTimeRange from '../DateTimeRange/DateTimeRange.vue'

export default {
	name: 'GroupedDateTimeRange',
	components: {
		Select,
		DateTimeRange
	},
	props: {
		/**
     * The label for the Select part
     */
		selectLabel: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The options for the Select's dropdown
     */
		selectItems: {
			type: Array,
			required: true,
			default: () => [],
			validator: (data) => {
				const conditions = []
				data.forEach((item) => {
					conditions.push(Object.keys(item).indexOf('label'))
					conditions.push(Object.keys(item).indexOf('value'))
				})

				return data.length === 0 || conditions.every(cond => cond > -1)
			}
		},
		/**
     * The GroupedDateTimeRange Select width. Defaults to 150px CSS width
		 * @values true, false
     */
		selectWidth: {
			type: Number,
			required: false,
			default: () => undefined
		},
		/**
     * The GroupedDateTimeRange FROM label
     */
		fromLabel: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The GroupedDateTimeRange TO label
     */
		toLabel: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The GroupedDateTimeRange data field
     */
		data: {
			type: Object,
			required: false,
			default: () => undefined
		},
		/**
     * The GroupedDateTimeRange input format (momentJS style)
     */
		inputFormat: {
			type: String,
			required: false,
			default: () => 'YYYY-MM-DDTHH:mm:ss.SSSZ'
		},
		/**
     * The GroupedDateTimeRange display format (momentJS style)
     */
		fieldFormat: {
			type: String,
			required: false,
			default: () => 'YYYY-MM-DD HH:mm'
		},
		/**
     * The GroupedDateTimeRange output format (momentJS style)
     */
		outputFormat: {
			type: String,
			required: false,
			default: () => 'YYYY-MM-DDTHH:mm:ss'
		},
		/**
     * If the GroupedDateTimeRange should be for dates only
     * @values true, false
     */
		onlyDate: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the GroupedDateTimeRange should be for time only
     * @values true, false
     */
		onlyTime: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the GroupedDateTimeRange is in an active state
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the GroupedDateTimeRange is disabled
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: () => false
		}
	},
	data () {
		return {
			selectData: undefined,
			rangeData: undefined
		}
	},
	computed: {
		groupedDateTimeRangeObjClass () {
			return {
				isActive: this.isActive
			}
		},
		selectStyleObj () {
			if (this.selectWidth) {
				return {
					width: `${this.selectWidth}px !important`
				}
			}

			return {}
		}
	},
	watch: {
		data: {
			handler (newVal) {
				if (newVal && Object.keys(newVal).length === 1) {
					const key = Object.keys(newVal)[0]
					this.selectData = key
					this.rangeData = newVal[`${key}`]
				} else if (this.selectItems && this.selectItems[0] && this.selectItems[0].value) {
					this.selectData = this.selectItems[0].value
				}
			},
			immediate: true
		}
	},
	methods: {
		handleSelectChange (val) {
			this.selectData = val
			this.$emit('change', this.selectData, this.rangeData)
		},
		handleDateChange (val) {
			this.rangeData = val || undefined
			/**
			 * Triggers when GroupedInputText changes it's value
			 * @event change
			 * @property {string} selectData new value set for the select part
			 * @property {array} rangeData new value set for the DateRange part
			 */
			this.$emit('change', this.selectData, this.rangeData)
		}
	}
}
</script>

<style lang="scss">
@import '../../scss/variables.scss';
.c-GroupedDateTimeRange {
  $select-fixed-width: 150px;

  width: 100%;
  border-radius: $border-radius;
  display: flex;
	flex: auto;

	&.isActive .c-GroupedDateTimeRange__select::before {
		content: '';
		width: 1px;
		height: calc(100% - 2px);
		position: absolute;
		top: 1px;
		right: 0px;
		z-index: 100;
		background-color: $un-white;
	}

	&.isActive .c-GroupedDateTimeRange__input > div:first-child::before {
		content: '';
		width: 1px;
		height: calc(100% - 4px);
		position: absolute;
		top: 2px;
		right: 0px;
		z-index: 100;
		background-color: $un-white;
	}

  &__select {
    width: $select-fixed-width !important;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-right: none !important;
  }

  &__input {
    width: calc(100% - #{$select-fixed-width});

    div:first-child .field-input {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
}
</style>
